/** Per-channel library pages (pages/library/*) + components/library/*, uploads, posters, TTS. */
export const library = {
  // Per-channel page titles + descriptions (pages/library/*).
  page: {
    ads: {
      title: "Ads library",
      description: "Uploaded ad spots for the ads channel.",
    },
    announcements: {
      title: "Announcements library",
      description: "Uploaded announcement audio for your venues.",
    },
    music: {
      title: "Music library",
      description: "Licensed catalogue and your music uploads.",
    },
    screen: {
      title: "Screen library",
      description: "Videos, images, web pages and widgets for your screens.",
    },
  },

  // Field labels reused across library forms/dialogs.
  field: {
    title: "Title",
    purpose: "Purpose",
    artist: "Artist",
    tags: "Tags",
    explicit: "Explicit content",
  },

  // Channel/purpose option labels. The video channel reads as "Video" in the
  // purpose pickers and "Screen" in the move-to-channel targets.
  channelLabel: {
    music: "Music",
    ads: "Ads",
    announcements: "Announcements",
    video: "Video",
    screen: "Screen",
  },

  // Source badges + player-bar subtitles.
  source: {
    catalogue: "Catalogue",
    upload: "Upload",
  },

  // Empty-state messages (music + uploads channel pages).
  empty: {
    uploadsNoMatch: "No uploads match your filters.",
    uploadsNone: "No uploads yet. Use New media to add files.",
    catalogueNoMatch: "No catalogue tracks match your filters.",
    itemsNoMatch: "No items match your filters.",
    uploadsNoneTitle: "No uploads yet",
    uploadsNoMatchTitle: "No matches",
    clearFilters: "Clear filters",
  },

  // Shared library page shell.
  shell: {
    playlistsInChannel: "Playlists in this channel",
    error: {
      title: "Couldn't load your library",
      description:
        "Something went wrong while loading your media. Check your connection and try again.",
    },
  },

  // Catalogue filter selects.
  filters: {
    sort: {
      placeholder: "Sort",
      title: "Title",
      mostPlayed: "Most played",
    },
    genre: { placeholder: "Genre", all: "All genres" },
    vocals: {
      placeholder: "Vocals",
      all: "All vocals",
      vocal: "Vocal",
      instrumental: "Instrumental",
    },
    mood: { placeholder: "Mood", all: "All moods" },
    venue: { placeholder: "Venue", all: "All venues" },
  },

  // Catalogue table + pagination.
  catalogue: {
    pagination: { range: "Catalogue: {start}–{end} of {total}" },
  },

  // "Play now" / "Cast to screen" one-click takeover on library rows.
  quickPlay: {
    playNow: "Play now",
    castToScreen: "Cast to screen",
    playNowOf: "Play {title} on a player now",
    castOf: "Cast {title} to a screen",
    dialogDescription:
      "Starts immediately on the chosen player and ends on its own.",
    targetLabel: "Play on",
    targetPlaceholder: "Choose a player",
    wholeLocation: "Whole location: {name}",
    devicesGroup: "Players",
    locationsGroup: "Locations",
    noDevices: "No players you can operate yet.",
    durationLabel: "Duration",
    dwellLabel: "Seconds on screen per showing",
    dwellHelp: "3–3600 seconds; capped to the chosen duration.",
    start: "Start",
    choice: {
      min15: "15 min",
      min30: "30 min",
      hour1: "1 hour",
      hours2: "2 hours",
      untilEndOfDay: "Until end of day",
    },
  },

  // Preview controls on catalogue + upload rows.
  preview: {
    pauseOf: "Pause preview of {title}",
    playOf: "Play preview of {title}",
    pause: "Pause preview",
    play: "Play preview",
    unavailable: "Preview unavailable",
  },

  // Bottom player-bar dock.
  playerBar: {
    prevTrack: "Previous track",
    play: "Play",
    pause: "Pause",
    nextTrack: "Next track",
    seek: "Seek",
    closePlayer: "Close player",
  },

  // Column picker + column header labels.
  columns: {
    button: "Columns",
    visibleFields: "Visible fields",
    reset: "Reset",
    labels: {
      title: "Title",
      purpose: "Purpose",
      vocal: "Vocal",
      source: "Source",
      artist: "Artist",
      album: "Album",
      genre: "Genre",
      mood: "Mood",
      bpm: "BPM",
      energy: "Energy",
      explicit: "Explicit",
      tags: "Tags",
      file: "File",
      format: "Format",
      size: "Size",
      dimensions: "Dimensions",
      added: "Added",
      duration: "Duration",
    },
  },

  // Media grid + table (uploads).
  media: {
    previewColumn: "Preview",
    actionsColumn: "Actions",
    selectItem: "Select {title}",
    editItem: "Edit {title}",
    deleteItem: "Delete {title}",
    editDialog: {
      title: "Edit media",
      artistPlaceholder: "e.g. The Skyline Trio",
      tagsPlaceholder: "e.g. upbeat, summer, lounge",
      tagsHelp: "Separate tags with commas",
    },
    confirmDeleteAsset: "Delete media asset?",
    mediaAssetsN: {
      one: "{count} media asset",
      other: "{count} media assets",
    },
    confirmDeleteMany: "Delete {label}?",
    toast: {
      updated: "Media updated",
      updateFailed: "Failed to update media",
      assetDeleted: "Asset deleted",
      deletedCount: "{count} deleted",
      deleteFailed: "Failed to delete media",
      movedCount: "{count} moved to {channel}",
      moveFailed: "Failed to move media",
      uploadedCount: "{count} uploaded",
      uploadedFailedCount: "{uploaded} uploaded, {failed} failed",
    },
    error: {
      storageUploadFailed: "Upload to storage failed",
      uploadFailed: "Upload failed",
    },
  },

  // Music page toolbar.
  music: {
    searchPlaceholder: "Search by title, artist, or album…",
    source: {
      placeholder: "Source",
      all: "All",
      uploads: "My uploads",
      catalogue: "Catalogue",
    },
    hideExplicit: "Hide explicit",
  },

  // Uploads-only channel page (ads / announcements / screen).
  uploadsPage: {
    searchPlaceholder: "Search by title…",
    type: {
      all: "All types",
      audio: "Audio",
      video: "Video",
      image: "Image",
    },
    gridView: "Grid view",
    tableView: "Table view",
    moveToChannelLabel: "Move to channel",
    moveToChannelPlaceholder: "Move to channel…",
  },

  // "Web pages & widgets" section on the screen library.
  signage: {
    section: {
      title: "Web pages & widgets",
      description:
        "Live dashboards, menus and built-in widgets you can schedule like any other screen content.",
    },
    newButtonLabel: "Web page / widget",
    empty:
      "Nothing yet. Add a web page (dashboard, menu, social wall) or a widget (clock, weather, ticker, QR, countdown, now playing, news, menu board).",
    widgets: {
      clock: "Clock",
      weather: "Weather",
      ticker: "Ticker",
      qr: "QR code",
      countdown: "Countdown",
      nowplaying: "Now playing",
      news: "News feed",
      menu: "Menu board",
    },
    refresh: {
      never: "Never (load once)",
      min1: "Every minute",
      min5: "Every 5 minutes",
      min15: "Every 15 minutes",
      hour1: "Every hour",
    },
    kind: { web: "Web page", widget: "Widget" },
    badge: { web: "Web", widget: "Widget" },
    confirmDelete: 'Delete "{title}"? Schedules using it go dark.',
    toast: { deleteFailed: "Couldn't delete", saveFailed: "Couldn't save" },
    dialog: {
      editTitle: "Edit web page / widget",
      newTitle: "New web page / widget",
      description:
        "Shown fullscreen on your screens when scheduled on the video channel.",
    },
    field: {
      pageUrl: "Page URL",
      reload: "Reload",
      fallbackImage: "Fallback image (if the page fails)",
      livePreview: "Live preview",
      widget: "Widget",
      timezoneOptional: "Timezone (optional)",
      latitude: "Latitude",
      longitude: "Longitude",
      labelOptional: "Label (optional)",
      messages: "Messages (one per line)",
      linkUrl: "Link URL",
      captionOptional: "Caption (optional)",
      countsDownTo: "Counts down to",
      whenDoneOptional: "When done (optional)",
      feedUrl: "Feed URL (RSS or Atom)",
      maxItems: "Headlines to show",
      rotateSec: "Seconds per headline",
      idleTextOptional: "When nothing is playing (optional)",
      menuHeadingOptional: "Heading (optional)",
      currencyOptional: "Currency suffix (optional)",
      sections: "Sections",
      sectionName: "Section name",
      itemName: "Item",
      itemPrice: "Price",
      itemDescription: "Description (optional)",
    },
    nowplaying: {
      showArtwork: "Show artwork",
      showUpNext: "Show what's up next",
    },
    menu: {
      addSection: "Add section",
      addItem: "Add item",
      removeSection: "Remove section",
      removeItem: "Remove item",
    },
    widgetPreviewEmpty:
      "Fill in the widget fields to see a live preview of what screens will show.",
    preview: {
      sampleTrack: "Golden Hour Glow",
      sampleArtist: "The Skyline Trio",
      samplePlaylist: "Afternoon Mix",
      sampleUpNext: "Slow Tide",
      sampleHeadline1: "Preview headline: your feed's stories appear here",
      sampleHeadline2: "A second story rotates in on your chosen cadence",
      sampleFeedSource: "Your news source",
    },
    fallback: { none: "None (branded slate)" },
    previewFrameTitle: "Preview",
    previewEmpty:
      "Enter a URL to preview it here. Some sites refuse to be embedded. If it stays blank here, it will not work on screens either.",
    clock: { hour12: "12-hour format" },
    placeholder: {
      titleWeb: "e.g. Sales dashboard",
      titleWidget: "e.g. Lobby clock",
      pageUrl: "https://…",
      weatherLabel: "Tallinn",
      qrUrl: "https://menu.example.com",
      timezone: "Device local, e.g. Europe/Tallinn",
      ticker: "Fresh croissants until 11:00\n2-for-1 smoothies today",
      qrCaption: "Scan for our menu",
      countdownLabel: "Grand opening",
      doneText: "We're open.",
      feedUrl: "https://example.com/feed.xml",
      idleText: "Enjoy your visit",
      menuHeading: "Lunch menu",
      currency: "\u20ac",
      sectionName: "Starters",
      itemName: "Tomato soup",
      itemPrice: "6.50",
      itemDescription: "With garlic croutons",
    },
    errors: {
      titleRequired: "Give it a title",
      urlInvalid: "Enter a valid http(s) URL",
      latRequired: "Weather needs a latitude",
      lonRequired: "Weather needs a longitude",
      tickerRequired: "Add at least one ticker line",
      qrUrlRequired: "QR needs a URL",
      countdownDateRequired: "Countdown needs a target date",
      feedUrlRequired: "Enter a valid http(s) feed URL",
      menuSectionRequired:
        "Add at least one section with a name and one named item",
    },
  },

  // Text-to-speech announcement dialog.
  tts: {
    trigger: "Text-to-speech",
    title: "Text-to-speech announcement",
    field: { text: "Announcement text", voice: "Voice" },
    placeholder: {
      text: "Attention shoppers: the store closes in ten minutes…",
    },
    voice: {
      select: "Select a voice",
      unavailable: "Text-to-speech unavailable",
    },
    voiceOption: "{label} ({description})",
    notConfigured: "Text-to-speech is not configured for this server.",
    generate: "Generate",
    generating: "Generating…",
    discard: "Discard",
    saveToLibrary: "Save to library",
    presets: {
      label: "Start from a preset",
      select: "Choose a preset…",
      hint: "Anything in {{double braces}} still needs filling in.",
    },
    ai: {
      label: "Or describe it and let AI draft the script",
      placeholder: "e.g. happy hour five to seven tonight, upbeat",
      draft: "Draft",
      drafting: "Drafting…",
      unavailable: "AI drafting isn't configured on this server yet.",
      limit: "Daily AI draft limit reached. Try again tomorrow.",
      failed: "Couldn't draft a script. Try rewording your description.",
    },
    error: { generateFailed: "Text-to-speech generation failed. Try again." },
    toast: {
      generateFailedTitle: "Couldn't generate speech",
      saved: "Announcement saved to library",
      renameFailedTitle: "Couldn't rename the announcement",
      renameFailedDescription:
        "It was generated and saved with its default title.",
    },
  },

  // Multi-file upload dialog.
  upload: {
    newMedia: "New media",
    title: "Upload Media",
    field: {
      files: "Files",
      displayDuration: "Display duration (seconds)",
    },
    selectPurpose: "Select purpose",
    titleForFile: "Title for {name}",
    artistForFile: "Artist for {name}",
    artistPlaceholder: "Artist (optional)",
    explicitLabel: "Explicit",
    removeFile: "Remove {name}",
    status: { done: "Done", failed: "Failed" },
    placeholder: { displayDuration: "Default" },
    uploading: "Uploading...",
    startUpload: "Start upload",
  },

  // One-time own-music licensing disclaimer.
  licensing: {
    title: "Using your own music",
    catalogueLicensed:
      "Every track in the OmniMedia catalogue is included in your subscription, and we charge no extra music-licensing fees. Collecting-society obligations vary by country and venue, so check what applies to you.",
    uploadsResponsibility:
      "Music you upload yourself is different: you are responsible for holding the rights to play it publicly in your venues (including any collecting-society fees that may apply in your country), as set out in our Terms of Service.",
    acknowledge: "I understand, continue",
  },
} as const;
